import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { useTran } from "@/hooks";
import { DataTable } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useOrganizationStore } from "@/features/organization/hooks";

const prettyLevel = (unitLevel) => {
  if (!unitLevel) return "—";
  if (unitLevel === "parent_company") return "Parent Company";
  const m = /^level_(\d+)$/i.exec(unitLevel);
  if (m) return `Level ${m[1]}`;
  return unitLevel;
};

const readCompanyId = (row) =>
  String(row?.companyId ?? row?.company?.id ?? row?.company ?? "");

const capitalizeWords = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");

const formatCountry = (row) => {
  const raw = row?.countryLabel || row?.country;
  if (!raw) return "—";
  return capitalizeWords(raw);
};

const OrganizationTableSection = observer(({ onEdit, onDelete }) => {
  const { t } = useTran();
  const store = useOrganizationStore();

  const pageIndex = Number(store.pageIndex ?? 1);
  const pageSize = Number(store.pageSize ?? 10);

  const allRows = useMemo(() => {
    const rows = store.filteredItems ?? store.items ?? [];

    const toTime = (v) => {
      const t = new Date(v || 0).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    return [...rows].sort(
      (a, b) => toTime(b?.createdAt) - toTime(a?.createdAt),
    );
  }, [store.filteredItems, store.items]);

  const total = allRows.length;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (pageIndex > maxPage) store.setPage?.(maxPage);
    if (pageIndex < 1) store.setPage?.(1);
  }, [total, pageSize, pageIndex, store]);

  const pageRows = useMemo(() => {
    const start = (pageIndex - 1) * pageSize;
    return allRows.slice(start, start + pageSize);
  }, [allRows, pageIndex, pageSize]);

  const columns = useMemo(
    () => [
      {
        key: "parentCompany",
        header: t("Parent Company"),
        render: (row) => {
          const id = readCompanyId(row);
          return store.companyNameById?.get?.(id) || "—";
        },
      },
      {
        key: "name",
        header: t("Unit Name"),
        render: (row) => (
          <div className="space-y-1">
            <div className="font-medium">{row?.name || "—"}</div>
            {row?.unitType ? (
              <div className="text-xs text-muted-foreground">
                {t("Type")}: {row.unitType}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: "code",
        header: t("Unit Code"),
        render: (row) =>
          row?.code ? <Badge variant="outline">{row.code}</Badge> : "—",
      },
      {
        key: "country",
        header: t("Country"),
        render: (row) => formatCountry(row),
      },
      {
        key: "unitLevel",
        header: t("Unit Level"),
        render: (row) => (
          <Badge variant="outline">{prettyLevel(row?.unitLevel)}</Badge>
        ),
      },
      {
        key: "actions",
        header: t("Actions"),
        align: "right",
        render: (row) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("Actions")}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>{t("Actions")}</DropdownMenuLabel>

                <DropdownMenuItem
                  className="gap-2"
                  onSelect={() => onEdit?.(row)}
                >
                  <Pencil className="h-4 w-4 text-emerald-600" />
                  {t("Edit")}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className="gap-2 text-destructive focus:text-destructive"
                  onSelect={() => onDelete?.(row)}
                >
                  <Trash2 className="h-4 w-4" />
                  {t("Delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [onDelete, onEdit, store.companyNameById, t],
  );

  return (
    <div className="rounded-radius border border-border bg-card p-4">
      <DataTable
        columns={columns}
        data={pageRows}
        loading={store.loading}
        getRowKey={(row) => row.id}
        emptyMessage={t("No organization unit found.")}
        pagination={{
          page: pageIndex,
          pageSize,
          total,
          onPageChange: store.setPage,
          onPageSizeChange: store.setPageSize,
          pageSizeOptions: [10, 20, 50],
        }}
      />
    </div>
  );
});

export default OrganizationTableSection;
